import { isLogin, isJumpOneLink, PhoneCell, userID } from '../store/index';

import { refreshOmegaParams } from './track';

import { getHost } from '@/utils/cluster';
import { getCookie, setCookie } from '@/utils/cookie';
import { updateCommonParams } from '@/utils/fmpTrack';
import { isPassportSdkReady } from '@/utils/waitPassportSdk';

export { isPassportSdkReady } from '@/utils/waitPassportSdk';

export interface LoginRes {
    ticket: string;
    cell: string;
    uid: string;
}

interface CheckLoginRes extends LoginRes {
    login: boolean;
}

const setCommonCookie = (cookie: string, data: string, expires: number) => {
    setCookie(cookie, data, {
        expires,
        domain: getHost(),
        path: '/'
    });
};

export const ensurePassportSdk = () => {
    if (!isPassportSdkReady()) {
        throw new Error('[MXCreditCardBALL] Passport SDK is not ready');
    }
};

/** 统一登录成功 payload（Passport 可能返回 uid / uid_str） */
export const normalizePassportLoginData = (
    data: Partial<LoginRes> & { uid_str?: string } | null | undefined
): LoginRes | null => {
    if (!data) return null;

    const ticket = data.ticket ? String(data.ticket) : '';
    const cell = data.cell !== null ? String(data.cell) : '';
    const uid = data.uid
        ? String(data.uid)
        : data.uid_str
            ? String(data.uid_str)
            : '';

    if (!ticket || !cell || !uid) return null;

    return { ticket, cell, uid };
};

export const clearLoginState = () => {
    isLogin.set(false);
    isJumpOneLink.set(false);
    userID.set('');
    setCommonCookie('uid', '', -1);
    setCommonCookie('cell', '', -1);
    setCommonCookie('ticket', '', -1);
    updateCommonParams({ uid: '', pub_uid: '' });
    refreshOmegaParams();
};

export const setCookieConfig = (res: LoginRes) => {
    const normalized = normalizePassportLoginData(res);

    if (!normalized) {
        console.error('[MXCreditCardBALL] Invalid login payload', res);

        return;
    }

    userID.set(normalized.uid);
    PhoneCell.set(normalized.cell);
    isLogin.set(true);
    isJumpOneLink.set(true);
    updateCommonParams({ uid: normalized.uid, pub_uid: normalized.uid });
    setCommonCookie('ticket', normalized.ticket, 1);
    setCommonCookie('uid', normalized.uid, 1);
    setCommonCookie('cell', normalized.cell, 1);
    refreshOmegaParams();
};

export const checkIsLogin = (): Promise<CheckLoginRes> => {
    const uid = getCookie('uid');
    const cell = getCookie('cell');
    const ticket = getCookie('ticket');

    if (ticket && uid && cell) {
        isLogin.set(true);
        userID.set(uid);
        PhoneCell.set(cell);
        updateCommonParams({ uid, pub_uid: uid });
        refreshOmegaParams();

        return Promise.resolve({
            login: true,
            ticket,
            uid,
            cell
        });
    }

    if (!isPassportSdkReady()) {
        clearLoginState();

        return Promise.resolve({
            login: false,
            ticket: '',
            uid: '',
            cell: ''
        });
    }

    return new Promise<CheckLoginRes>((resolve, reject) => {
        window.login.isLogin(
            (response: CheckLoginRes) => {
                const {
                    login,
                    ticket: loginTicket,
                    cell: loginCell,
                    uid: loginUid
                } = response;

                if (login) {
                    setCookieConfig({
                        ticket: loginTicket,
                        uid: loginUid,
                        cell: loginCell
                    });
                } else {
                    clearLoginState();
                }

                resolve(response);
            },
            (error: string) => {
                console.log(error);
                reject(error);
            }
        );
    }).catch(() => {
        clearLoginState();

        return {
            login: false,
            ticket: '',
            uid: '',
            cell: ''
        };
    });
};

export const initPassportSDK = () => {
    try {
        if (!window.login?.setConfig) {
            console.error('[initPassportSDK] Passport SDK not loaded');

            return;
        }

        window.login.setConfig({
            appid: 200112,
            role: 1,
            lang: 'es-MX',
            showProtocol: false,
            styleObj: {
                btn: {
                    activeColor: '#FFFFFF',
                    bgColor: '#FF5F14'
                }
            },
            country_calling_code: '+52',
            country_id: 484,
            canonical_country_code: 'MX',
            isHalfScreen: false,
            customPages: [
                'code',
                'newCode',
                'password',
                'verifyMessage',
                'captcha',
                'phone',
                'checkEmail',
                'checkIdentity',
                'toast'
            ],
            extra_info: window.adv
                ? {
                    ...window.adv.getAdvExtraInfoForPassport(),
                    is_use_slider: window.isUseSlider ? 1 : 0
                }
                : {
                    is_use_slider: window.isUseSlider ? 1 : 0
                }
        });
    } catch (e: unknown) {
        console.error('[initPassportSDK error]', e);
    }
};

export const logout = (): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
        try {
            if (window.login?.logout) {
                window.login.logout(
                    () => resolve(true),
                    () => resolve(true)
                );

                return;
            }
        } catch (e) {
            console.error('[logout error]', e);
        }

        resolve(true);
    }).finally(() => {
        clearLoginState();
    });
};
